import { useParams } from 'react-router-dom';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import { Loader } from '../../../../../../components';
import { format, parseISO } from 'date-fns';
import useGetCatalogInventoryDetails from '../../../../../../queries/catalogInvectoryDetails/useGetCatalogInventoryDetails';

const fieldsToRender = [
  // Global Catalog Information
  { key: 'globalCatalog.applicationName', label: 'Application Name' },
  { key: 'globalCatalog.filename', label: 'Catalog Filename' },
  { key: 'firstLastSeen', label: 'Seen' },
  // Environment-Specific Signing Information
  { key: 'signer.subject', label: 'Signer Certificate Subject' },
  { key: 'signer.thumbPrint', label: 'Signer Certificate Thumbprint' },
  { key: 'signer.serialNumber', label: 'Signer Certificate Serial Number' },
  { key: 'signerValidity', label: 'Signer Certificate Validity' },
  { key: 'issuer.subject', label: 'Issuer Certificate Subject' },
  { key: 'issuer.thumbPrint', label: 'Issuer Certificate Thumbprint' },
  { key: 'issuer.serialNumber', label: 'Issuer Certificate Serial Number' },
  { key: 'issuerValidity', label: 'Issuer Certificate Validity' },
  { key: 'isSigned', label: 'Signed' },
];

const Container = ({ children }) => (
  <Box
    sx={theme => ({
      backgroundColor: '#484848',
      boxShadow: '0px 1px 2px 0px #64748B1A',
      borderRadius: theme.spacing(1),
      padding: '32px 24px',
      width: '100%',
    })}>
    {children}
  </Box>
);

const CatalogInventoryDetails = () => {
  const { catalogInventoryId } = useParams();
  const { isLoading, data: catalogInventoryDetails } = useGetCatalogInventoryDetails({
    catalogInventoryId,
  });

  function getNestedValue(path) {
    return path.split('.').reduce((acc, key) => acc?.[key], catalogInventoryDetails);
  }

  const getCellValue = key => {
    if (key === 'title') return null;

    if (key === 'signerValidity') {
      const signerFrom = catalogInventoryDetails.signer?.validFrom;
      const signerTo = catalogInventoryDetails.signer?.validTo;

      if (!signerFrom || !signerTo) {
        return 'no data';
      }

      const formattedSignerFrom = format(parseISO(signerFrom), 'yyyy-MM-dd');
      const formattedSignerTo = format(parseISO(signerTo), 'yyyy-MM-dd');

      return `${formattedSignerFrom} - ${formattedSignerTo}`;
    }

    if (key === 'issuerValidity') {
      const issuerFrom = catalogInventoryDetails.issuer?.validFrom;
      const issuerTo = catalogInventoryDetails.issuer?.validTo;

      if (!issuerFrom || !issuerTo) {
        return 'no data';
      }

      const formattedIssuerFrom = format(parseISO(issuerFrom), 'yyyy-MM-dd');
      const formattedIssuerTo = format(parseISO(issuerTo), 'yyyy-MM-dd');

      return `${formattedIssuerFrom} - ${formattedIssuerTo}`;
    }

    if (key.includes('globalCatalog') || key.includes('signer') || key.includes('issuer')) {
      return getNestedValue(key) || 'no data';
    }
    if (key === 'isSigned') {
      return catalogInventoryDetails[key].toString();
    }

    if (key === 'firstLastSeen') {
      const firstSeen = catalogInventoryDetails.firstSeen
        ? format(parseISO(catalogInventoryDetails.firstSeen), 'yyyy-MM-dd')
        : 'no data';

      const lastSeen = catalogInventoryDetails.lastSeen
        ? format(parseISO(catalogInventoryDetails.lastSeen), 'yyyy-MM-dd')
        : 'no data';

      return (
        <Box>
          First: {firstSeen}, Last: {lastSeen}
        </Box>
      );
    }

    if (key === 'firstSeen') {
      return catalogInventoryDetails.firstSeen
        ? format(parseISO(catalogInventoryDetails.firstSeen), 'yyyy-MM-dd')
        : 'no data';
    }

    if (key === 'lastSeen') {
      return catalogInventoryDetails.lastSeen
        ? format(parseISO(catalogInventoryDetails.lastSeen), 'yyyy-MM-dd')
        : 'no data';
    }

    return catalogInventoryDetails[key] ?? 'no data';
  };

  if (isLoading) {
    return (
      <Box mt={'80px'}>
        <Loader />
      </Box>
    );
  }

  return (
    <Container>
      <Stack spacing={2}>
        <Table>
          <TableBody>
            {fieldsToRender.map(({ key, label }) => (
              <TableRow key={key}>
                <TableCell>{label}:</TableCell>
                <TableCell>{getCellValue(key)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Stack>
    </Container>
  );
};

export default CatalogInventoryDetails;
